import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  "Lifetime membership — you never pay the £5.99 monthly fee, ever",
  "Founding badge on your profile — priority shift matching",
  "Early access to supervisor & door tiers",
  "Direct line to the GuardGig28 ops team",
];

export default function FoundingPass() {
  const { user } = useAuth();
  const [providers, setProviders] = useState({ stripe: true });
  const [loading, setLoading] = useState("");
  const [avail, setAvail] = useState(null);

  const refreshAvail = () =>
    fetch(`${API}/payments/founding-pass`).then((r) => r.json()).then(setAvail).catch(() => {});

  useEffect(() => {
    fetch(`${API}/payments/providers`).then((r) => r.json()).then(setProviders).catch(() => {});
    refreshAvail();
  }, []);

  const claimed = avail?.claimed ?? 0;
  const cap = avail?.cap ?? 500;
  const closed = !!avail?.closed;

  const payStripe = async () => {
    setLoading("stripe");
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lookup_key: "founding_pass", origin_url: window.location.origin, user_id: user?.user_id || "" }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else {
        toast.error(data.detail || "Checkout failed to start.");
        refreshAvail();
      }
    } catch {
      toast.error("Checkout failed to start.");
    } finally {
      setLoading("");
    }
  };

  const payPaypal = async () => {
    setLoading("paypal");
    try {
      const res = await fetch(`${API}/payments/paypal/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin, user_id: user?.user_id || "" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      if (data.approve_url) window.location.href = data.approve_url;
    } catch (e) {
      toast.error(e.message || "PayPal failed to start.");
      refreshAvail();
    } finally {
      setLoading("");
    }
  };

  const paypalActive = !!providers.paypal;

  return (
    <section id="founding-pass" data-testid="founding-pass-section" className="relative overflow-hidden py-24 md:py-32">
      <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[400px] rounded-full bg-amber-500/8 blur-[120px]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <p className="font-mono-tag text-[11px] text-amber-500">Limited to the first 500 members</p>
            <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl">
              The Founding Pass
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
              A one-time pass for guards and venues who believe in the rules from day one.
              Founding members keep their edge forever — and their badge proves it.
            </p>
            <ul className="mt-8 space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" strokeWidth={3} />
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6"
          >
            <div className="glow-amber rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#111520] to-[#0b0f16] p-10">
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500 text-[#07090e]">
                  <Crown className="h-6 w-6" strokeWidth={2} />
                </span>
                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 font-mono-tag text-[9px] text-amber-400">One-time</span>
              </div>
              <p className="font-display mt-8 text-7xl font-black leading-none text-slate-50">
                £39<span className="text-2xl text-slate-500"> once</span>
              </p>
              <p className="mt-3 text-sm text-slate-500">One-time. Pay by card or PayPal.</p>

              <div data-testid="founding-counter" className="mt-6">
                <div className="flex items-center justify-between font-mono-tag text-[10px] uppercase text-amber-400">
                  <span>{claimed} / {cap} claimed</span>
                  <span>{Math.max(0, cap - claimed)} left</span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                  <div
                    data-testid="founding-progress"
                    className="h-full rounded-full bg-amber-500 transition-[width] duration-700"
                    style={{ width: `${Math.min(100, (claimed / cap) * 100)}%` }}
                  />
                </div>
              </div>

              {closed ? (
                <div data-testid="founding-sold-out" className="mt-8 rounded-md border border-red-500/30 bg-red-500/5 p-5 text-center">
                  <p className="font-display text-lg font-bold uppercase text-red-400">
                    All 500 claimed — sold out
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    The Founding Pass is gone. Membership is £5.99/month — sign in and claim or post a shift to subscribe.
                  </p>
                </div>
              ) : (
                <>
                  <button
                    data-testid="pay-stripe-btn"
                    onClick={payStripe}
                    disabled={!!loading}
                    className="mt-8 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-amber-500 font-display text-lg font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 hover:shadow-[0_0_30px_rgba(245,158,11,0.35)] disabled:opacity-50"
                  >
                    {loading === "stripe" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Get the pass · Card"}
                  </button>

                  <button
                    data-testid="pay-paypal-btn"
                    onClick={paypalActive ? payPaypal : undefined}
                    disabled={!paypalActive || !!loading}
                    title={paypalActive ? "Pay with PayPal" : "PayPal activates at launch"}
                    className={`mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-md border font-display text-lg font-bold uppercase transition-all disabled:opacity-50 ${
                      paypalActive
                        ? "border-white/15 bg-white/5 text-slate-50 hover:border-amber-500/40"
                        : "cursor-not-allowed border-white/5 text-slate-600"
                    }`}
                  >
                    {loading === "paypal" ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Get the pass · PayPal
                        {!paypalActive && <span className="font-mono-tag text-[9px] normal-case opacity-70">(at launch)</span>}
                      </>
                    )}
                  </button>
                </>
              )}
              <p className="mt-5 text-center font-mono-tag text-[8px] text-slate-600">
                {closed ? "Founding window closed · membership £5.99/month" : "One-time · lifetime membership · secure checkout"}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
